/**
 * How we help section for level pages.
 *
 * What: Explains DeepThink's teaching approach for addressing level-specific challenges.
 * Why: Demonstrates how the platform and lessons directly solve the problems outlined in the challenges section.
 * When: Update when changing approach section structure or layout.
 */
import { Box, Paper, Stack, Typography } from '@mui/material';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSection from '@/components/landing/LandingSection';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

interface HelpPoint {
  title: string;
  description: string;
}

export interface HowWeHelpSectionContent {
  title: string;
  body?: string;
  points: HelpPoint[];
  closingLine?: string;
}

interface HowWeHelpSectionProps {
  content: HowWeHelpSectionContent;
}

function HowWeHelpSection({ content }: HowWeHelpSectionProps) {
  const titleId = 'how-we-help-title';

  return (
    <LandingSection
      id="how-we-help"
      tone="transparent"
      ariaLabelledby={titleId}
    >
      <Stack
        spacing={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <LandingCenteredSectionHead
          titleId={titleId}
          title={content.title}
          body={content.body}
        />

        <Stack spacing={{ xs: 3, sm: 3.5 }} sx={{ width: '100%', minWidth: 0 }}>
          {content.points.map((point, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                p: { xs: 3, sm: 3.5, md: 4 },
                borderRadius: radii.md,
                backgroundColor: colors.paper,
                border: `1px solid ${colors.border}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack spacing={2} sx={{ width: '100%', minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      mt: 0.25,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: colors.washCobalt,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: colors.accentCobalt,
                      }}
                    />
                  </Box>
                  <Typography
                    component="h3"
                    sx={{
                      fontSize: { xs: 18, sm: 19 },
                      fontWeight: 700,
                      color: colors.ink,
                      lineHeight: 1.3,
                      minWidth: 0,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {point.title}
                  </Typography>
                </Stack>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.inkMuted,
                    lineHeight: 1.65,
                    fontSize: { xs: 15, sm: 16 },
                    pl: { xs: 0, sm: '44px' },
                    minWidth: 0,
                    overflowWrap: 'break-word',
                  }}
                >
                  {point.description}
                </Typography>
              </Stack>
            </Paper>
          ))}

          {content.closingLine && (
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                p: { xs: 2.5, sm: 3 },
                borderRadius: radii.md,
                backgroundColor: colors.washCobalt,
                border: `1px solid ${colors.accentCobaltLight}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 15, sm: 16 },
                  color: colors.ink,
                  lineHeight: 1.65,
                  fontWeight: 500,
                  fontStyle: 'italic',
                  overflowWrap: 'break-word',
                }}
              >
                {content.closingLine}
              </Typography>
            </Paper>
          )}
        </Stack>
      </Stack>
    </LandingSection>
  );
}

export default HowWeHelpSection;
